import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { format, isAfter } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  Button,
  Checklist,
  DatePicker,
  Drawer,
  Field,
  Input,
  Modal,
  Loader
} from '@itds/elements/index.js';
import { STATES } from '@itds/Constants';
import { Colors } from '@itds/Theme';

import Api from '@carrier/utils/Api';

const VersionForm = ({ form, formTags, productGroups, onClose, onSave, show }) => {
  const styles = {
    footer_btns: {
      display: 'flex'
    },
    published_footer: {
      background: '#444',
      color: '#fff',
      padding: 20,
      margin: '-20px -30px',
      fontWeight: 600,
      fontSize: 14,
      textAlign: 'center',
      flex: 1
    },
    publish_date: {
      width: '100%',
      marginRight: -15
    },
    file_preview: {
      position: 'relative',
      cursor: 'pointer',
      background: '#f5f5f5',
      borderRadius: 4,
      overflow: 'hidden',
      minHeight: 450,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center'
    },
    file_button: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      background: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column'
    },
    file_error: {
      border: `1px solid ${Colors.RED.hex}`
    },
    link: {
      color: Colors.BLUE.hex,
      fontWeight: 600,
      marginTop: 20,
      cursor: 'pointer',
      textAlign: 'center'
    },
    loader: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      background: 'rgba(255,255,255,0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1
    }
  };
  const [errors, setErrors] = useState([]);
  const [show_file_button, toggleFileButton] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scheduled, setScheduled] = useState(true);
  const [data, setData] = useState({
    id: null,
    form_id: null,
    carrier_id: null,
    name: '',
    number: '',
    description: '',
    states: [],
    form_tag_ids: [],
    product_group_ids: [],
    file: '',
    publish_date: formatInTimeZone(Date.now(), 'UTC', 'yyyy-MM-dd')
  });
  const [modal, setModal] = useState(false);

  const newFileInput = React.createRef();
  const read_only = form.id && !scheduled;

  useEffect(() => {
    const new_form = form || {};
    // Always set form_id to form.form_id or fallback to form.id
    setData({
      id: new_form.id,
      form_id: new_form.form_id || new_form.id,
      carrier_id: new_form.carrier_id,
      name: new_form.name || '',
      number: new_form.number || '',
      description: new_form.description || '',
      states: new_form.states || [],
      form_tag_ids: new_form.form_tag_ids || [],
      product_group_ids: new_form.product_group_ids || [],
      file: '',
      publish_date: formatInTimeZone(new_form.publish_date || Date.now(), 'UTC', 'yyyy-MM-dd')
    });
    setScheduled(new_form.id ? isAfter(new Date(new_form.publish_date), Date.now()) : true);
  }, [form]);

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleChecklistChange = (name, selected) => {
    setData({ ...data, [name]: selected });
  };

  const handleDateChange = date => {
    setData({ ...data, publish_date: format(new Date(date), 'yyyy-MM-dd') });
    setScheduled(isAfter(new Date(date), Date.now()));
  };

  const handleFileChange = e => {
    setData({ ...data, file: e.target.files[0] });
  };

  const changeFile = () => {
    newFileInput.current.click();
  };

  const viewFile = () => {
    if (form.file && form.file.url) {
      window.open(form.file.url);
    }
  };

  const handleSave = () => {
    const errors = [];
    if (!data.name || data.name.length < 3) {
      errors.push('name');
    }
    if (!data.number || data.number.length < 3) {
      errors.push('number');
    }
    setErrors(errors);
    if (!errors.length) {
      // Always use a valid form_id (from data or fallback to form.id)
      const form_id = data.form_id || form.id;
      const { form_id: _omit, file, ...form_versions_attributes } = data;
      setSaving(true);
      if (file) {
        form_versions_attributes.file = file;
      }
      Api.saveForm(form_id, { carrier_id: data.carrier_id, form_versions_attributes }).then(() => {
        setSaving(false);
        onSave('add');
      });
    }
  };

  const handleDelete = () => {
    Api.saveForm(data.form_id, {
      form_versions_attributes: {
        id: data.id,
        _destroy: true
      }
    }).then(() => {
      setModal(true);
    });
  };

  const confirmDelete = () => {
    window.scrollTo(0, 0);
    setModal(null);
    onSave('delete');
  };

  return (
    <Drawer
      alignRight={true}
      footer={
        read_only ? (
          <div style={styles.published_footer}>
            Published: {formatInTimeZone(data.publish_date, 'UTC', 'MMM do, yyyy')}
          </div>
        ) : (
          <div style={{ flex: 1 }}>
            <div style={styles.footer_btns}>
              <DatePicker future={true} onChange={handleDateChange} value={data.publish_date} />
              <Button onClick={handleSave} style={{ marginLeft: 10 }}>
                {scheduled ? 'Schedule' : 'Publish'}
              </Button>
            </div>
            {scheduled && data.id ? (
              <div
                onClick={handleDelete}
                style={Object.assign({}, styles.link, { color: Colors.RED.hex })}
              >
                Delete
              </div>
            ) : null}
          </div>
        )
      }
      onClose={onClose}
      show={show}
    >
      {saving ? (
        <Loader />
      ) : (
        <>
          <Field label='PDF File'>
            <input
              onChange={handleFileChange}
              ref={newFileInput}
              style={{ display: 'none' }}
              type='file'
            />
            <div
              onMouseEnter={toggleFileButton.bind(null, true)}
              onMouseLeave={toggleFileButton.bind(null, false)}
              style={Object.assign(
                {},
                styles.file_preview,
                errors.includes('file') && styles.file_error
              )}
            >
              {data.file ? (
                <>
                  <i
                    className='mdi mdi-check-circle'
                    style={{ fontSize: 100, color: Colors.GREEN.hex }}
                  />
                  <p>{data.file.name}</p>
                </>
              ) : (
                <>
                  {form.file && form.file.web_thumb.url ? (
                    <img onClick={viewFile} src={form.file.web_thumb.url} />
                  ) : (
                    <i
                      className='mdi mdi-file-question'
                      style={{ fontSize: 100, color: '#e5e5e5' }}
                    />
                  )}
                </>
              )}

              {show_file_button && !read_only ? (
                <div style={styles.file_button}>
                  <Button onClick={changeFile}>Select File</Button>
                  {data.file ? (
                    <div onClick={() => setData({ ...data, file: '' })} style={styles.link}>
                      Revert
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </Field>
          <Field label='Name'>
            <Input
              invalid={errors.includes('name')}
              onChange={handleInputChange.bind(null, 'name')}
              readOnly={read_only}
              required={true}
              value={data.name}
            />
          </Field>
          <Field label='Number'>
            <Input
              invalid={errors.includes('number')}
              onChange={handleInputChange.bind(null, 'number')}
              readOnly={read_only}
              required={true}
              value={data.number}
            />
          </Field>
          <Field label='Decription'>
            <Input
              height={200}
              onChange={handleInputChange.bind(null, 'description')}
              readOnly={read_only}
              type='textarea'
              value={data.description}
            />
          </Field>
          <Field label='Product Groups'>
            <Checklist
              onChange={handleChecklistChange.bind(null, 'product_group_ids')}
              options={productGroups.map(pg => ({ value: pg.id, label: pg.group_name }))}
              readOnly={read_only}
              selected={data.product_group_ids}
            />
          </Field>
          <Field label='Tags'>
            <Checklist
              onChange={handleChecklistChange.bind(null, 'form_tag_ids')}
              options={formTags.map(ft => ({ value: ft.id, label: ft.name }))}
              readOnly={read_only}
              selected={data.form_tag_ids}
            />
          </Field>
          <Field label='States'>
            <Checklist
              onChange={handleChecklistChange.bind(null, 'states')}
              options={STATES}
              readOnly={read_only}
              selected={data.states}
              showSelectAll={true}
            />
          </Field>
        </>
      )}
      {modal ? (
        <Modal
          buttons={[
            {
              color: 'RED',
              children: 'Confirm Delete',
              onClick: confirmDelete,
              style: { marginLeft: 'auto' }
            }
          ]}
          contained={true}
          onClose={() => {
            setModal(null);
          }}
          title={`Delete "${form.name}"`}
        >
          <div style={{ padding: 40, fontSize: 16, textAlign: 'center', lineHeight: '1.5em' }}>
            Are you sure you want to delete "{form.name}"?
          </div>
        </Modal>
      ) : null}
    </Drawer>
  );
};

VersionForm.propTypes = {
  form: PropTypes.object,
  formTags: PropTypes.array,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  productGroups: PropTypes.array,
  show: PropTypes.bool
};

export default VersionForm;
